// The message box (mountVoiceUI and the phone page): a textarea that wraps and grows with what is
// typed, up to a share of the window, then scrolls; Enter sends, Shift+Enter starts a new line.

/** The box grows to at most this share of the window's height, then scrolls. */
export const COMPOSE_MAX_SHARE = 0.4;

export interface AutoGrowOptions {
  /** Share of `within` the box may take. Default {@link COMPOSE_MAX_SHARE}. */
  share?: number;
  /** The height the share is of. Default: the visible window. A layout that gives the box's
   * column less than the window (a side pane below it) passes that column's height. */
  within?: () => number;
}

/**
 * Makes `box` grow with its text (and shrink back when it is cleared). Returns `fit`, to call
 * after changing `box.value` from code (a send clears it).
 */
export function autoGrow(box: HTMLTextAreaElement, opts: AutoGrowOptions = {}): () => void {
  const view = box.ownerDocument.defaultView;
  const windowHeight = () => view?.visualViewport?.height || view?.innerHeight || 800;
  const fit = () => {
    const height = opts.within?.() || windowHeight();
    const max = Math.max(40, Math.round(height * (opts.share ?? COMPOSE_MAX_SHARE)));
    box.style.height = "auto";
    // scrollHeight has the padding but not the borders (the box is border-box).
    const borders = box.offsetHeight - box.clientHeight;
    const want = box.scrollHeight + Math.max(0, borders);
    box.style.height = `${Math.min(want, max)}px`;
    box.style.overflowY = want > max ? "auto" : "hidden";
  };
  box.addEventListener("input", fit);
  view?.addEventListener("resize", fit);
  fit();
  return fit;
}

/** Enter sends `form`; Shift+Enter (and Enter while an input method is composing) types a newline. */
export function enterSends(box: HTMLTextAreaElement, form: HTMLFormElement): void {
  box.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || e.shiftKey || e.isComposing || e.ctrlKey || e.altKey || e.metaKey) return;
    e.preventDefault();
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { cancelable: true }));
  });
}
